import { first } from 'rxjs/operators';
import { TrackedLinkService, TrackedLink, LinkAnalytics, SourceStats } from '@app/_components/tracked-link';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TrackedLinkFormComponent } from '../tracked-link/tracked-link.component';
import { LinkAnalyticsComponent } from '../link-analytics/link-analytics.component';
import { TemplateAnalyticsComponent } from '../template-analytics/template-analytics.component';
import { CampaignAnalyticsComponent } from '../campaign-analytics/campaign-analytics.component';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { Clipboard } from '@angular/cdk/clipboard';
import { ReservationService } from '@app/_components/reservation/reservation.service';
import { MsgService } from '@app/_components/msg';
import { InboxComponent } from '@app/_components/inbox/inbox/inbox.component';

@Component({
    selector: 'app-tracked-link-list',
    templateUrl: './tracked-link-list.component.html',
    styleUrls: ['./tracked-link-list.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [],
    standalone: false
})
export class TrackedLinkListComponent implements OnInit {

	// Track loading state per reservationId for open order
	openingOrder: { [key: number]: boolean } = {};

	constructor(
		private trackedLinkService: TrackedLinkService,
		private reservationService: ReservationService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private dialog: MatDialog,
		private clipboard: Clipboard,
		private msgService: MsgService
	) {
		this.trackingModes = trackedLinkService.trackingModes;
		this.sourceTypes = trackedLinkService.sourceTypes;
		this.statuses = trackedLinkService.statuses;
	}

	// data
	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	trackedLinks: any;
	trackedLink: TrackedLink;

	// paging
	first = 0;
	rows = 25;

	// list
	datasource: any;
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];

	// forms
	formLoading = false;

	// dropdowns
	trackingModes: any;
	sourceTypes: any;
	statuses: any;

	// date range filter
	createDateFrom: string = '';
	createDateTo: string = '';

	// summary
	summary: LinkAnalytics;
	sourceStats: SourceStats[] = [];
	summaryLoading = true;


	// topic name map
	topicMap: { [key: number]: string } = {
		100: 'Reservation',
		200: 'Campaign',
		300: 'IMAP Read',
		400: 'Jobs',
		500: 'Owner Profile',
		600: 'Task',
		700: 'Proforma',
		800: 'Website Query',
		900: 'Custom Mail',
		1000: 'Public Review',
		1100: 'Sent Review Request',
		1200: 'Survey Guest Exit',
		1300: 'Created Lead Profile',
		1400: 'Inquiry Waikiki',
		1500: 'Inquiry Influencer',
		1600: 'SMS Received',
		1700: 'Send SMS',
		1800: 'Review Notification'
	};

	columns: any[] = [
		{ field: 'trackedLinkId', header: 'Id', width: '70px' },
		{ field: 'name', header: 'Name', width: 'auto' },
		{ field: 'trackingMode', header: 'Mode', width: '100px' },
		{ field: 'sourceType', header: 'Source', width: '100px' },
		{ field: 'status', header: 'Status', width: '90px' },
		{ field: 'templateId', header: 'Template', width: '80px' },
		{ field: 'topic', header: 'Topic', width: '110px' },
		{ field: 'topicId', header: 'Topic Id', width: '80px' },
		{ field: 'mktgCampaignId', header: 'Campaign', width: '80px' },
		{ field: 'clickCount', header: 'Clicks', width: '80px' },
		{ field: 'uniqueClickCount', header: 'Unique', width: '80px' },
		{ field: 'lastClickedAt', header: 'Last Click', width: '140px' },
		{ field: 'createDate', header: 'Created', width: '140px' },
	];

	ngOnInit(): void {
		this.multiSortMeta = [];
		this.multiSortMeta.push({ field: 'createDate', order: -1 });
		this.loadSummary();
	}

	// ─── Summary Cards ────────────────────────────────────────────────────

	loadSummary() {
		this.summaryLoading = true;
		this.trackedLinkService.getSummary().pipe(first()).subscribe(
			(res: any) => {
				this.summary = res.data.analytics;
			},
			(err: any) => { console.log('Summary error: ' + JSON.stringify(err)); },
			() => { this.summaryLoading = false; }
		);

		this.trackedLinkService.getBySource().pipe(first()).subscribe(
			(res: any) => {
				this.sourceStats = res.data.analytics?.sourceStats || [];
			},
			(err: any) => { console.log('By source error: ' + JSON.stringify(err)); }
		);
	}

	// ─── Template Analytics — New Tab ────────────────────────────────────

	openTemplateAnalyticsTab(templateId?: number) {
		const params = new URLSearchParams();
		if (templateId) { params.set('templateId', String(templateId)); }
		params.set('dateTo', new Date().toISOString().substring(0, 10));
		window.open('/template-analytics?' + params.toString(), '_blank');
	}

	openCampaignAnalyticsTab(campaignId?: number) {
		const params = new URLSearchParams();
		if (campaignId) { params.set('campaignId', String(campaignId)); }
		params.set('dateTo', new Date().toISOString().substring(0, 10));
		window.open('/campaign-analytics?' + params.toString(), '_blank');
	}

	// ─── Template Analytics Dialog ───────────────────────────────────────

	onOpenTemplateAnalytics(templateId?: number) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = false;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '1100px';
		dialogConfig.maxHeight = '90vh';
		dialogConfig.data = {};
		if (templateId) {
			dialogConfig.data = { templateId: templateId, dateFrom: '', dateTo: new Date().toISOString().substring(0, 10) };
		}

		this.dialog.open(TemplateAnalyticsComponent, dialogConfig);
	}

	// ─── Campaign Analytics Dialog ──────────────────────────────────────

	onOpenCampaignAnalytics(campaignId?: number) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = false;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '1300px';
		dialogConfig.maxHeight = '90vh';
		dialogConfig.data = {};
		if (campaignId) {
			dialogConfig.data = { campaignId: campaignId, dateFrom: '', dateTo: new Date().toISOString().substring(0, 10) };
		}

		this.dialog.open(CampaignAnalyticsComponent, dialogConfig);
	}

	// ─── Lazy Load Table ──────────────────────────────────────────────────

	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			this.lazyLoadEvent = event;

			// Sync component paging state from the lazy load event
			this.first = event.first || 0;
			if (event.rows && event.rows > 0) { this.rows = event.rows; }

			if ((!event.filters || Object.keys(event.filters).length === 0) && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			const pageNumber = Math.floor(this.first / this.rows);
			this.trackedLinkService.findByFilter(
				pageNumber, this.rows,
				event.sortField, event.sortOrder,
				event.multiSortMeta, event.globalFilter, event.filters,
				this.createDateFrom || undefined, this.createDateTo || undefined
			).pipe(first()).subscribe(
				(res: any) => {
					this.trackedLinks = res.data.trackedLinks;
					this.recordTotal = res.page.recordTotal;
				},
				(err: any) => {
					console.log('TrackedLink list error: ' + JSON.stringify(err));
					this.alertService.error(err);
				},
				() => { this.loading = false; }
			);
		} catch (error) {
			console.log('loadLazy error: ' + error);
		}
	}

	// ─── Row Select → Analytics ───────────────────────────────────────────

	onRowSelect(event: any) {
		this.onOpenAnalytics(event.data);
	}

	// ─── Copy Tracking URL ────────────────────────────────────────────────

	copyTrackingUrl(link: TrackedLink) {
		this.clipboard.copy(link.trackingUrl);
		this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Tracking URL copied to clipboard', life: 2000 });
	}

	// ─── Open Add Form ────────────────────────────────────────────────────

	onOpenFormAdd() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '700px';
		dialogConfig.data = { title: 'Create Tracked Link' };

		const dialogRef = this.dialog.open(TrackedLinkFormComponent, dialogConfig);

		dialogRef.componentInstance.doAdd.subscribe(data => {
			this.addCB(data);
			dialogRef.close();
		});
	}

	// ─── Open Edit Form ───────────────────────────────────────────────────

	onOpenFormEdit(link: TrackedLink) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '700px';
		dialogConfig.data = { trackedLink: link, title: 'Edit Tracked Link' };

		const dialogRef = this.dialog.open(TrackedLinkFormComponent, dialogConfig);

		dialogRef.componentInstance.doUpdate.subscribe(data => {
			this.updateCB(data);
			dialogRef.close();
		});

		dialogRef.componentInstance.doDelete.subscribe(data => {
			this.deleteCB(data);
			dialogRef.close();
		});
	}

	// ─── Open Analytics Detail ────────────────────────────────────────────

	onOpenAnalytics(link: TrackedLink) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = false;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '900px';
		dialogConfig.maxHeight = '90vh';
		dialogConfig.data = { trackedLink: link };

		this.dialog.open(LinkAnalyticsComponent, dialogConfig);
	}

	// ─── Delete Confirm ───────────────────────────────────────────────────

	onRowDelete(event: any) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to archive link #' + event.trackedLinkId + '?',
			header: 'Confirm Archive',
			icon: 'pi pi-exclamation-triangle',
			accept: () => { this.formDelete(event); },
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Archive canceled.', life: 3000 });
			}
		});
	}

	formDelete(link: any) {
		try {
			this.formLoading = true;
			this.trackedLinkService.delete(link.trackedLinkId).subscribe(
				(res: any) => {
					link.status = 'ARCHIVED';
					this.trackedLinks = [...this.trackedLinks];
					this.messageService.add({ severity: 'success', summary: 'Archived', detail: 'Link #' + link.trackedLinkId + ' archived.', life: 3000 });
				},
				(err: any) => {
					this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not archive link.', life: 3000 });
				},
				() => { this.formLoading = false; }
			);
		} catch (error) {
			this.alertService.error(error);
		}
	}

	// ─── Callbacks ────────────────────────────────────────────────────────

	addCB(link: TrackedLink) {
		if (!this.trackedLinks) { this.trackedLinks = []; }
		this.trackedLinks.unshift(link);
		this.trackedLinks = [...this.trackedLinks];
		this.messageService.add({ severity: 'success', summary: 'Created', detail: 'Link #' + link.trackedLinkId + ' created.', life: 3000 });
		this.loadSummary();
	}

	updateCB(link: TrackedLink) {
		let idx = this.trackedLinks.findIndex((item: any) => item.trackedLinkId === link.trackedLinkId);
		if (idx >= 0) { this.trackedLinks[idx] = link; }
		this.trackedLinks = [...this.trackedLinks];
		this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Link #' + link.trackedLinkId + ' updated.', life: 3000 });
	}

	deleteCB(link: TrackedLink) {
		this.trackedLinks.splice(this.trackedLinks.indexOf(link), 1);
		this.trackedLinks = [...this.trackedLinks];
		this.messageService.add({ severity: 'success', summary: 'Archived', detail: 'Link #' + link.trackedLinkId + ' archived.', life: 3000 });
		this.loadSummary();
	}

	// ─── Helpers ──────────────────────────────────────────────────────────

	getModeName(mode: string): string {
		const found = this.trackingModes.find((m: any) => m.value === mode);
		return found ? found.name : mode || '';
	}

	getSourceName(source: string): string {
		const found = this.sourceTypes.find((s: any) => s.value === source);
		return found ? found.name : source || '';
	}

	getTopicName(topic: number): string {
		if (!topic) return '';
		return this.topicMap[topic] || String(topic);
	}

	openMessage(msgId: number) {
		if (!msgId) { return; }
		this.msgService.findById(msgId).pipe(first()).subscribe(
			(res: any) => {
				const msg = res.data.msg;
				if (!msg) {
					this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'Message ' + msgId + ' not found.', life: 3000 });
					return;
				}
				const dialogConfig = new MatDialogConfig();
				dialogConfig.width = '100%';
				dialogConfig.height = '100%';
				dialogConfig.maxWidth = '1600px';
				dialogConfig.maxHeight = '800px';
				dialogConfig.disableClose = false;
				dialogConfig.autoFocus = true;
				dialogConfig.hasBackdrop = true;
				// Ensure msgId is set — InboxComponent.ngOnInit() uses it to determine
				// read mode vs compose mode. If the API response omits msgId, fall back
				// to the ID used to fetch the message.
				if (!msg.msgId) { msg.msgId = msgId; }
				dialogConfig.data = { inbox: msg, title: 'View Message' };
				this.dialog.open(InboxComponent, dialogConfig);
			},
			(err: any) => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load message ' + msgId, life: 3000 });
			}
		);
	}

	openOrder(reservationId: number, event: any) {
		event.stopPropagation();
		if (!reservationId || this.openingOrder[reservationId]) { return; }
		this.openingOrder[reservationId] = true;
		this.reservationService.getOrderIdByReservationId(reservationId).pipe(first()).subscribe(
			(res: any) => {
				window.open(`https://admin.kbmhawaii.com/order/${res?.data?.orderId}`, '_blank');
				this.openingOrder[reservationId] = false;
			},
			() => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch order for reservation ' + reservationId, life: 3000 });
				this.openingOrder[reservationId] = false;
			}
		);
	}

	getStatusClass(status: string): string {
		switch (status) {
			case 'ACTIVE': return 'status-active';
			case 'PAUSED': return 'status-paused';
			case 'EXPIRED': return 'status-expired';
			case 'ARCHIVED': return 'status-archived';
			case 'DRAFT': return 'status-draft';
			default: return '';
		}
	}

	applyDateFilter() {
		this.first = 0;
		if (this.table) {
			this.table.first = 0;
			this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
		}
	}

	clearDateFilter() {
		this.createDateFrom = '';
		this.createDateTo = '';
		this.applyDateFilter();
	}

	reloadPage() { window.location.reload(); }

	// ─── Custom Sticky Paginator ──────────────────────────────────────────

	@ViewChild('pt') table: Table;

	get currentPage(): number {
		return Math.floor(this.first / this.rows) + 1;
	}

	get totalPages(): number {
		return this.recordTotal ? Math.ceil(this.recordTotal / this.rows) : 1;
	}

	isFirstPage(): boolean { return this.first === 0; }

	isLastPage(): boolean {
		return !this.recordTotal || (this.first + this.rows) >= this.recordTotal;
	}

	onPageChange(newFirst: number) {
		if (newFirst < 0) { newFirst = 0; }
		const maxFirst = (this.totalPages - 1) * this.rows;
		if (newFirst > maxFirst) { newFirst = maxFirst; }
		this.first = newFirst;

		// Trigger a lazy load with new offset
		if (this.table) {
			this.table.first = this.first;
			this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
		}
	}

	onRowsChange(val: string) {
		this.rows = parseInt(val, 10);
		this.first = 0;
		if (this.table) {
			this.table.rows = this.rows;
			this.table.first = 0;
			this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
		}
	}
}
